import { stripTypename } from "@apollo/client/utilities";
import {
  Button,
  Callout,
  Card,
  ControlGroup,
  FormGroup,
  InputGroup,
  MenuItem,
  NumericInput,
  ProgressBar,
  Switch,
} from "@blueprintjs/core";
import { Select } from "@blueprintjs/select";
import {
  getComponentHelperText,
  getComponentIntent,
  type SaveToolbarProps,
  TEMPLATE_TO_DISPLAY,
} from "@package/ui-apollo-components";
import { HeaderCreateEdit } from "@package/ui-components";
import {
  type Animation,
  type AnimationOption,
  CreateLayoutDocument,
  DeleteLayoutDocument,
  GetLayoutDocument,
  GetLayoutGridDocument,
  GetLayoutListNameIdDocument,
  type Layout,
  type LayoutCustomModel,
  Templates,
  TileRotatePattern,
  UpdateLayoutDocument,
  useGetLayoutQuery,
} from "@package/ui-gql/index-data-access";
import { useUpdateSelectedOrganization } from "@package/ui-organization";
import type { ValidationError } from "class-validator";
import { produce, type WritableDraft } from "immer";
import { useCallback, useEffect, useState } from "react";
import {
  ANIMATION_TYPE_TO_DISPLAY,
  EDIT_CARD_WIDTH_CLASS,
  SidebarEntryPath,
  TEMPLATE_TO_TILE_COUNT,
} from "../../../../constants/contants";
import { SaveToolbar } from "../../../SaveToolbar/SaveToolbar";
import { createDefaultAnimationOption } from "./AnimationOptions/animation-option-utils";
import { AnimationOptionFadeComponent } from "./AnimationOptions/AnimationOptionFade";
import { AnimationOptionFlipComponent } from "./AnimationOptions/AnimationOptionFlip";
import { AnimationOptionWipeComponent } from "./AnimationOptions/AnimationOptionWipe";

const defaultAnimation: Animation = {
  enabled: true,
  option: {
    type: "flip",
    direction: TileRotatePattern.Horizontal,
    duration: 3,
  },
};
const defaultLayout: Layout = {
  animation: defaultAnimation,
  borderRadius: 0,
  content: {
    staleFlyoutTimeout: 60,
    tileContentUpdateFrequency: 15,
    tileContentUpdatePct: 1,
  },
  name: "",
  organization: "",
  padding: 1,
  tileCount: 0,
};
const defaultLayoutCustom: LayoutCustomModel = {
  height: 0,
  width: 0,
  positions: [],
};

type LayoutEditProps = {
  id?: string;
  toolbarProps?: Partial<SaveToolbarProps>;
};

export function LayoutEdit({ id, toolbarProps }: LayoutEditProps) {
  const [layoutState, setLayoutState] = useState<Layout>(defaultLayout);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);

  const updateLayout = useCallback(
    (callback: (draft: WritableDraft<Layout>) => void) => {
      setLayoutState((prev) => produce(prev, callback));
    },
    [],
  );

  const {
    data: dataLayout,
    loading: loadingLayout,
    error: errorLayout,
  } = useGetLayoutQuery({
    variables: {
      id: id ?? "",
    },
    skip: !id,
  });

  useEffect(
    function effectOnLayoutDataChanged() {
      if (dataLayout?.item) {
        setLayoutState(dataLayout.item);
      }
    },
    [dataLayout?.item],
  );

  useUpdateSelectedOrganization(dataLayout?.item?.organization);

  if (loadingLayout) {
    return (
      <Card elevation={1}>
        <ProgressBar intent="primary" stripes animate />
      </Card>
    );
  }

  if (errorLayout) {
    return (
      <Callout intent="danger" title="Failed Loading Layout">
        <p>Failed loading layout item: {id}</p>
      </Callout>
    );
  }

  return (
    <div className="flex flex-col">
      <HeaderCreateEdit
        editing={!!id}
        loading={loadingLayout}
        title="Layout"
        name={dataLayout?.item?.name}
      />
      <Card elevation={1} className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup
            label="Name"
            labelFor="control-name"
            labelInfo="(required)"
            disabled={disabled}
            intent={getComponentIntent<Layout>("name", errors)}
            helperText={getComponentHelperText<Layout>("name", errors)}
          >
            <InputGroup
              disabled={disabled}
              id="control-name"
              placeholder="Name"
              value={layoutState.name ?? ""}
              onValueChange={(value) =>
                updateLayout((draft) => {
                  draft.name = value;
                })
              }
            />
          </FormGroup>
        </div>
      </Card>
      <Card className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup
            disabled={disabled}
            label="Template"
            intent={getComponentIntent<Layout>("template", errors)}
            helperText={getComponentHelperText<Layout>("template", errors)}
          >
            <Select<Templates>
              activeItem={
                layoutState.template &&
                Object.values(Templates).includes(
                  layoutState.template as Templates,
                )
                  ? (layoutState.template as Templates)
                  : Templates.Custom
              }
              disabled={disabled}
              fill
              filterable={false}
              items={Object.values(Templates)}
              itemRenderer={(
                template,
                { handleClick, handleFocus, modifiers },
              ) => (
                <MenuItem
                  active={modifiers.active}
                  disabled={modifiers.disabled}
                  key={template}
                  onClick={handleClick}
                  onFocus={handleFocus}
                  roleStructure="listoption"
                  text={TEMPLATE_TO_DISPLAY[template]}
                />
              )}
              popoverProps={{ minimal: true }}
              onItemSelect={(template) =>
                updateLayout((draft) => {
                  draft.template = template;
                  draft.tileCount = TEMPLATE_TO_TILE_COUNT[template];
                })
              }
            >
              <Button
                alignText="left"
                disabled={disabled}
                fill
                minimal
                outlined
                rightIcon="caret-down"
                text={
                  layoutState.template
                    ? TEMPLATE_TO_DISPLAY[layoutState.template]
                    : "Select a Template"
                }
              />
            </Select>
          </FormGroup>
          <ControlGroup fill>
            <FormGroup
              disabled={disabled}
              label="Padding"
              intent={getComponentIntent<Layout>("padding", errors)}
              helperText={getComponentHelperText<Layout>("padding", errors)}
            >
              <NumericInput
                min={0}
                disabled={disabled}
                allowNumericCharactersOnly
                fill
                value={layoutState.padding ?? 0}
                onValueChange={(value) => {
                  updateLayout((draft) => {
                    draft.padding = value;
                  });
                }}
              />
            </FormGroup>
            <FormGroup
              disabled={disabled}
              className="ml-2"
              label="Border Radius"
              intent={getComponentIntent<Layout>("borderRadius", errors)}
              helperText={getComponentHelperText<Layout>(
                "borderRadius",
                errors,
              )}
            >
              <NumericInput
                min={0}
                disabled={disabled}
                allowNumericCharactersOnly
                fill
                value={layoutState.borderRadius ?? 0}
                onValueChange={(value) => {
                  updateLayout((draft) => {
                    draft.borderRadius = value;
                  });
                }}
              />
            </FormGroup>
          </ControlGroup>
          <FormGroup
            disabled={disabled}
            label="Content Rotate Frequency"
            fill
            intent={getComponentIntent("tileContentUpdateFrequency", errors)}
            helperText={getComponentHelperText(
              "tileContentUpdateFrequency",
              errors,
              "The amount of time in seconds before new content is loaded into the layout.",
            )}
          >
            <NumericInput
              min={0}
              disabled={disabled}
              allowNumericCharactersOnly
              fill
              value={layoutState.content?.tileContentUpdateFrequency ?? 0}
              onValueChange={(value) => {
                updateLayout((draft) => {
                  draft.content = {
                    ...draft.content,
                    tileContentUpdateFrequency: value,
                  };
                });
              }}
            />
          </FormGroup>
          <FormGroup
            disabled={disabled}
            label="Content Rotation Percent"
            fill
            intent={getComponentIntent("tileContentUpdatePct", errors)}
            helperText={getComponentHelperText(
              "tileContentUpdatePct",
              errors,
              "The percentage of content that will be updated.",
            )}
          >
            <NumericInput
              min={0}
              max={100}
              disabled={disabled}
              allowNumericCharactersOnly
              fill
              value={(layoutState.content?.tileContentUpdatePct ?? 0) * 100}
              onValueChange={(value) => {
                updateLayout((draft) => {
                  draft.content = {
                    ...draft.content,
                    tileContentUpdatePct: value / 100,
                  };
                });
              }}
            />
          </FormGroup>
          <FormGroup
            disabled={disabled}
            label="No Interaction Timeout"
            fill
            intent={getComponentIntent("staleFlyoutTimeout", errors)}
            helperText={getComponentHelperText(
              "staleFlyoutTimeout",
              errors,
              "The amount of time in seconds before a flyout will close from no user input.",
            )}
          >
            <NumericInput
              min={0}
              disabled={disabled}
              allowNumericCharactersOnly
              fill
              value={layoutState.content?.staleFlyoutTimeout ?? 0}
              onValueChange={(value) => {
                updateLayout((draft) => {
                  draft.content = {
                    ...draft.content,
                    staleFlyoutTimeout: value,
                  };
                });
              }}
            />
          </FormGroup>
          <FormGroup
            disabled={disabled || layoutState.template !== Templates.Custom}
            label="Content Count"
            fill
            intent={getComponentIntent<Layout>("tileCount", errors)}
            helperText={getComponentHelperText<Layout>("tileCount", errors)}
          >
            <NumericInput
              disabled={disabled || layoutState.template !== Templates.Custom}
              min={0}
              allowNumericCharactersOnly
              fill
              value={layoutState.tileCount ?? 0}
              onValueChange={(value) => {
                updateLayout((draft) => {
                  draft.tileCount = value;
                });
              }}
            />
          </FormGroup>
        </div>
      </Card>
      <Card className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup disabled={disabled}>
            <Switch
              disabled={disabled}
              label="Animation Enabled"
              checked={disabled || !!layoutState.animation?.enabled}
              onChange={() =>
                updateLayout((draft) => {
                  draft.animation = {
                    ...defaultAnimation,
                    ...draft.animation,
                    enabled: !draft.animation?.enabled,
                  };
                })
              }
            />
          </FormGroup>
          <FormGroup
            disabled={disabled || !layoutState.animation?.enabled}
            label="Animation Type"
            fill
          >
            <Select<NonNullable<AnimationOption["__typename"]>>
              activeItem={
                layoutState.animation?.option?.__typename ??
                "AnimationOptionFlip"
              }
              disabled={disabled || !layoutState.animation?.enabled}
              fill
              filterable={false}
              items={[
                "AnimationOptionFade",
                "AnimationOptionFlip",
                "AnimationOptionWipe",
              ]}
              itemRenderer={(type, { handleClick, handleFocus, modifiers }) => (
                <MenuItem
                  active={modifiers.active}
                  disabled={modifiers.disabled}
                  key={type}
                  onClick={handleClick}
                  onFocus={handleFocus}
                  roleStructure="listoption"
                  text={ANIMATION_TYPE_TO_DISPLAY[type]}
                />
              )}
              popoverProps={{ minimal: true }}
              onItemSelect={(type) =>
                updateLayout((draft) => {
                  draft.animation = {
                    ...defaultAnimation,
                    ...draft.animation,
                    option: createDefaultAnimationOption(type),
                  };
                })
              }
            >
              <Button
                alignText="left"
                disabled={disabled || !layoutState.animation?.enabled}
                fill
                minimal
                outlined
                rightIcon="caret-down"
                text={
                  layoutState.animation?.option?.__typename
                    ? ANIMATION_TYPE_TO_DISPLAY[
                        layoutState.animation.option.__typename
                      ]
                    : "Select an Animation"
                }
              />
            </Select>
          </FormGroup>
          {layoutState.animation?.option?.__typename ===
            "AnimationOptionFade" && (
            <AnimationOptionFadeComponent
              disabled={disabled || !layoutState.animation?.enabled}
              option={layoutState.animation.option}
              onOptionUpdated={(option) => {
                updateLayout((draft) => {
                  draft.animation = {
                    ...defaultAnimation,
                    ...draft.animation,
                    option,
                  };
                });
              }}
            />
          )}
          {layoutState.animation?.option?.__typename ===
            "AnimationOptionFlip" && (
            <AnimationOptionFlipComponent
              disabled={disabled || !layoutState.animation?.enabled}
              option={layoutState.animation.option}
              onOptionUpdated={(option) => {
                updateLayout((draft) => {
                  draft.animation = {
                    ...defaultAnimation,
                    ...draft.animation,
                    option,
                  };
                });
              }}
            />
          )}
          {layoutState.animation?.option?.__typename ===
            "AnimationOptionWipe" && (
            <AnimationOptionWipeComponent
              disabled={disabled || !layoutState.animation?.enabled}
              option={layoutState.animation.option}
              onOptionUpdated={(option) => {
                updateLayout((draft) => {
                  draft.animation = {
                    ...defaultAnimation,
                    ...draft.animation,
                    option,
                  };
                });
              }}
            />
          )}
        </div>
      </Card>
      {layoutState.template === Templates.Custom && (
        <Card className="mt-4">
          <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
            <FormGroup disabled={disabled} label="Custom Layout Canvas Width">
              <NumericInput
                min={0}
                disabled={disabled}
                allowNumericCharactersOnly
                fill
                value={layoutState.custom?.width ?? 0}
                onValueChange={(value) => {
                  updateLayout((draft) => {
                    draft.custom = draft.custom ?? { ...defaultLayoutCustom };
                    draft.custom.width = value;
                  });
                }}
              />
            </FormGroup>
            <FormGroup disabled={disabled} label="Custom Layout Canvas Height">
              <NumericInput
                min={0}
                disabled={disabled}
                allowNumericCharactersOnly
                fill
                value={layoutState.custom?.height ?? 0}
                onValueChange={(value) => {
                  updateLayout((draft) => {
                    draft.custom = draft.custom ?? { ...defaultLayoutCustom };
                    draft.custom.height = value;
                  });
                }}
              />
            </FormGroup>
            {layoutState.custom?.positions?.map((position, index) => (
              <FormGroup
                key={index}
                disabled={disabled}
                label={`Custom Position ${index + 1}`}
              >
                <ControlGroup fill>
                  <FormGroup disabled={disabled} label="X">
                    <NumericInput
                      min={0}
                      disabled={disabled}
                      allowNumericCharactersOnly
                      fill
                      value={position.x}
                      onValueChange={(value) => {
                        updateLayout((draft) => {
                          draft.custom = draft.custom ?? {
                            ...defaultLayoutCustom,
                          };
                          draft.custom.positions[index].x = value;
                        });
                      }}
                    />
                  </FormGroup>
                  <FormGroup disabled={disabled} label="Y">
                    <NumericInput
                      min={0}
                      disabled={disabled}
                      allowNumericCharactersOnly
                      fill
                      value={position.y}
                      onValueChange={(value) => {
                        updateLayout((draft) => {
                          draft.custom = draft.custom ?? {
                            ...defaultLayoutCustom,
                          };
                          draft.custom.positions[index].y = value;
                        });
                      }}
                    />
                  </FormGroup>
                  <FormGroup disabled={disabled} label="Width">
                    <NumericInput
                      min={0}
                      disabled={disabled}
                      allowNumericCharactersOnly
                      fill
                      value={position.width}
                      onValueChange={(value) => {
                        updateLayout((draft) => {
                          draft.custom = draft.custom ?? {
                            ...defaultLayoutCustom,
                          };
                          draft.custom.positions[index].width = value;
                        });
                      }}
                    />
                  </FormGroup>
                  <FormGroup disabled={disabled} label="Height">
                    <NumericInput
                      min={0}
                      disabled={disabled}
                      allowNumericCharactersOnly
                      fill
                      value={position.height}
                      onValueChange={(value) => {
                        updateLayout((draft) => {
                          draft.custom = draft.custom ?? {
                            ...defaultLayoutCustom,
                          };
                          draft.custom.positions[index].height = value;
                        });
                      }}
                    />
                  </FormGroup>
                  <FormGroup label={<>{"\u00A0"}</>}>
                    <Button
                      disabled={disabled}
                      icon="trash"
                      intent="danger"
                      minimal
                      onClick={() => {
                        updateLayout((draft) => {
                          draft.custom = draft.custom ?? {
                            ...defaultLayoutCustom,
                          };
                          draft.custom.positions.splice(index, 1);
                        });
                      }}
                    />
                  </FormGroup>
                </ControlGroup>
              </FormGroup>
            ))}
            <Button
              disabled={disabled}
              icon="add"
              intent="primary"
              minimal
              onClick={() => {
                updateLayout((draft) => {
                  draft.custom = draft.custom ?? { ...defaultLayoutCustom };
                  draft.custom.positions.push({
                    x: 0,
                    y: 0,
                    width: 0,
                    height: 0,
                  });
                });
              }}
            >
              Add Custom Position
            </Button>
          </div>
        </Card>
      )}
      <Card className="mt-4">
        <div className={`flex ${EDIT_CARD_WIDTH_CLASS}`}>
          <SaveToolbar
            documentCreate={CreateLayoutDocument}
            documentDelete={DeleteLayoutDocument}
            documentUpdate={UpdateLayoutDocument}
            path={SidebarEntryPath.layout}
            isNew={!id}
            refetchQueries={[
              GetLayoutDocument,
              GetLayoutGridDocument,
              GetLayoutListNameIdDocument,
            ]}
            title="Layout"
            validationModel="layout-update"
            variablesCreate={stripTypename(layoutState)}
            variablesUpdate={stripTypename(layoutState)}
            variablesDelete={{ id }}
            {...toolbarProps}
            onInflight={(inflight) => {
              setDisabled(inflight);

              if (inflight) {
                setErrors([]);
              }
            }}
            onValidationFailed={(errors) => {
              setErrors(errors);
            }}
          />
        </div>
      </Card>
    </div>
  );
}
